// <Composer> — canonical ClientHub kit component (Epic 1 · S1.5).
//
// Writes through fn_compose_send ONLY (00_CONTRACT.md §11). The single write
// path for a message in the whole app. Channel picker, unmissable "who can
// see this", agent-draft Release + Edit (distinct affordances). A blocked send
// returns the message to draft with the reason in metadata.send_blocked.
//
// RPC shape is UNCONFIRMED (see api/hub.js). Sends defensively.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { composeSend, createClientNote } from "../../../api/hub";
import { ErrorState } from "../../shared/StateMessage";

// The channels the DB CHECK constraint (ops_messages_channel_chk) accepts —
// verified live: chat, email, in_app, task_comment. A DM is NOT a channel; it is
// the boolean is_dm on the message (set by the thread, never by the channel),
// so "dm" must never appear here. Verified 2026-09-11 against 203,791 rows.
const CHANNELS = [
  { key: "email", label: "Email" },
  { key: "chat", label: "Chat" },
  { key: "in_app", label: "In-app" },
  { key: "task_comment", label: "Task comment" },
];

/**
 * @param {object} props
 * @param {boolean} [props.noteToggle=false]  Replaces the channel picker with an
 *   Email | Note segmented control. Email sends via fn_compose_send on the email
 *   channel; Note writes an ops_notes row for the team (createClientNote) — needs a
 *   `clientId`. Off by default, so other callers (e.g. InboxDrawer) are unchanged.
 * @param {number} [props.focusSignal]  A counter the parent bumps to focus the
 *   textarea. Not acted on at mount, only when the value changes.
 */
export default function Composer({
  channel: initialChannel = "email",
  threadId = null, taskId = null, clientId = null,
  isAgentDraft = false, isDm = false, onSent, onCancel, className = "", initialBody = "",
  noteToggle = false, focusSignal = 0,
}) {
  const [channel, setChannel] = useState(initialChannel);
  // "email" | "note" — only meaningful when noteToggle is on.
  const [mode, setMode] = useState("email");
  const isNote = noteToggle && mode === "note";
  const [body, setBody] = useState(initialBody);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(null);
  // A short confirmation after a successful send, so a cleared textarea is not the
  // only sign it worked (toggle mode only). Cleared by the next edit or toggle.
  const [notice, setNotice] = useState(null);
  const textareaRef = useRef(null);

  // Focus on request from the parent (the header's Compose button). The first value
  // is the baseline, so mounting never steals focus.
  const lastFocusSignal = useRef(focusSignal);
  useEffect(() => {
    if (focusSignal !== lastFocusSignal.current) {
      lastFocusSignal.current = focusSignal;
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [focusSignal]);

  const handleSend = useCallback(async () => {
    if (!body.trim() || sending) return;
    setSending(true); setError(null); setBlocked(null); setNotice(null);
    try {
      if (isNote) {
        // A note is an observation for the team, not a message: not pinned. A failed
        // insert (e.g. the caller's row-level security) surfaces below and keeps the
        // draft — it is never reported as sent.
        const res = await createClientNote({ clientId, body, isPinned: false });
        setBody(""); setNotice("Note added"); onSent?.(res?.data);
        return;
      }
      const res = await composeSend({
        p_channel: noteToggle ? "email" : channel, p_body: body,
        p_thread_id: threadId, p_task_id: taskId, p_client_id: clientId,
      });
      const data = res?.data;
      if (data && data.send_blocked) {
        setBlocked({ message: data.send_blocked.reason || data.send_blocked.message || "Send blocked." });
        setSending(false); return;
      }
      setBody(""); if (noteToggle) setNotice("Email sent"); onSent?.(data);
    } catch (err) { setError(err?.message || "Couldn't send."); }
    finally { setSending(false); }
  }, [body, sending, channel, threadId, taskId, clientId, onSent, isNote, noteToggle]);

  // Agent draft: Edit returns to draft — the OPPOSITE of Release (spec §4).
  const handleEdit = useCallback(() => {
    setBlocked(null); setError(null); textareaRef.current?.focus();
  }, []);

  const canSend = body.trim().length > 0 && !sending;

  // Switching Email <-> Note is a new intent: a failure or a blocked-send warning from
  // the other mode no longer applies, and a stale confirmation would mislead.
  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next); setError(null); setBlocked(null); setNotice(null);
  };

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 ${className}`}
      data-testid="composer">
      <div className="flex items-center gap-2 mb-2">
        {noteToggle ? (
          <div role="group" aria-label="Send as" className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            {[["email", "Email"], ["note", "Note"]].map(([key, label]) => (
              <button key={key} type="button" aria-pressed={mode === key} onClick={() => switchMode(key)}
                className={`h-9 px-4 text-sm font-medium transition-colors ${
                  mode === key ? "bg-[#32B1FF] text-white" : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
                }`}>
                {label}
              </button>
            ))}
          </div>
        ) : (
          <select value={channel} onChange={(e) => setChannel(e.target.value)}
            className="text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-gray-700 dark:text-gray-200"
            aria-label="Channel">
            {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}
        <span className="text-xs text-gray-400">
          {isDm ? "Visible to 2 people" : "Visible to the team"}
        </span>
      </div>
      <textarea ref={textareaRef}
        className="w-full rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
        rows={3} value={body} aria-label="Message" placeholder={isNote ? "Write a note for the team…" : "Write a reply…"}
        onChange={(e) => { setBody(e.target.value); setNotice(null); }}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend(); } }} />
      {blocked && (
        <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2">
          <p className="text-sm text-amber-700 dark:text-amber-400">{blocked.message}</p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">Your message is saved as a draft.</p>
        </div>
      )}
      {error && <div className="mt-2"><ErrorState variant="strip" message={error} /></div>}
      {/* Always mounted, text swapped: a live region that appears together with its text
          is announced unreliably, one that already exists and changes is. Empty it takes
          no space (empty:m-0). Only in toggle mode — other callers get no new node. */}
      {noteToggle && (
        <p role="status" data-testid="composer-notice" className="mt-2 text-sm font-medium text-emerald-700 empty:m-0 dark:text-emerald-400">{notice}</p>
      )}
      <div className="mt-2 flex items-center justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white">Cancel</button>
        )}
        {isAgentDraft && (
          <button type="button" onClick={handleEdit}
            className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-1 rounded-md border border-gray-200 dark:border-gray-700">
            Edit
          </button>
        )}
        <button type="button" onClick={handleSend} disabled={!canSend}
          className="text-sm font-medium text-white bg-[#32B1FF] px-3 py-1 rounded-md hover:bg-[#1a9aef] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="composer-send">
          {isAgentDraft ? "Release" : sending ? (isNote ? "Saving…" : "Sending…") : isNote ? "Add note" : noteToggle ? "Send email" : "Send"}
        </button>
      </div>
    </div>
  );
}

